import React from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import { PAYMENT_METHOD_FORM_SCROLL_BODY } from '../../../constants/layoutSpacing';
import { cpfDigitsOnly, maskCpf, maskPersonName } from 'utils/masks';

const PAYMENT_METHOD_CARDS = [
  {
    value: 'PIX',
    label: 'PIX',
    icon: 'Smartphone',
    description: 'Pagamento instantâneo no ambiente seguro do Mercado Pago',
    eta: 'Liberação em até 1 hora após confirmação',
  },
  {
    value: 'CREDIT_CARD',
    label: 'Cartão de Crédito',
    icon: 'CreditCard',
    description: 'Visa, Mastercard, Elo e outras bandeiras',
    eta: 'Confirmação imediata após aprovação',
  },
];

const PaymentMethodFormContent = ({
  paymentType,
  onPaymentTypeChange,
  customerName,
  onCustomerNameChange,
  customerCpf,
  onCustomerCpfChange,
  accountEmail,
  errorMessage,
  license,
  sdkLoading,
  sdkError,
  sandboxCheckout,
  testPayerEmail,
  checkoutHints,
}) => {
  return (
    <div className={PAYMENT_METHOD_FORM_SCROLL_BODY}>
      <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 flex gap-3">
        <Icon name="ExternalLink" size={20} className="text-primary flex-shrink-0 mt-0.5" />
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">
            Pagamento seguro via Mercado Pago
          </p>
          <p className="text-xs text-muted-foreground">
            {paymentType === 'CREDIT_CARD'
              ? `Preencha seus dados e o cartão aqui no Neo Modelos. O pagamento é processado com segurança pelo Mercado Pago para ${license?.license_name}.`
              : `Você concluirá o pagamento com ${license?.license_name}. Usamos o e-mail da sua conta Neo Modelos e pedimos nome e CPF — exigência do Mercado Pago.`}
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <p className="text-sm font-medium text-foreground">Escolha a forma de pagamento</p>
        <div className="grid grid-cols-1 gap-3">
          {PAYMENT_METHOD_CARDS.map(method => {
            const selected = paymentType === method.value;

            return (
              <button
                key={method.value}
                type="button"
                onClick={() => onPaymentTypeChange(method.value)}
                className={[
                  'w-full text-left rounded-lg border p-4 transition-smooth',
                  selected
                    ? 'border-primary bg-primary/10 shadow-elevation-sm'
                    : 'border-border bg-muted/20 hover:border-primary/30',
                ].join(' ')}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name={method.icon} size={20} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h4 className="text-sm font-semibold text-foreground">{method.label}</h4>
                      {selected && (
                        <Icon name="CheckCircle2" size={18} className="text-primary flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{method.description}</p>
                    <p className="text-xs text-muted-foreground mt-2">{method.eta}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {sandboxCheckout && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 space-y-2">
          <p className="text-sm font-medium text-foreground">Ambiente de teste Mercado Pago</p>
          {paymentType === 'CREDIT_CARD' ? (
            <div className="text-xs text-muted-foreground space-y-2">
              <p>
                Abra em <strong>aba anônima</strong> e <strong>não faça login</strong> no checkout do Mercado Pago.
                Pague como convidado com o cartão de teste.
              </p>
              <p>
                Cartão: <span className="font-mono text-foreground">5031 4332 1540 6351</span> · CVV{' '}
                <span className="font-mono text-foreground">123</span> · validade{' '}
                <span className="font-mono text-foreground">11/30</span>
              </p>
              <p>
                Nome do titular: <span className="font-mono text-foreground">APRO</span> (aprovado) ou{' '}
                <span className="font-mono text-foreground">OTHE</span> (recusado) · CPF{' '}
                <span className="font-mono text-foreground">12345678909</span>
              </p>
            </div>
          ) : (
            testPayerEmail && (
              <p className="text-xs text-muted-foreground">
                E-mail do comprador de teste:{' '}
                <span className="font-mono text-foreground">{testPayerEmail}</span>
              </p>
            )
          )}
          <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-1">
            {(checkoutHints || []).map(hint => (
              <li key={hint}>{hint}</li>
            ))}
          </ul>
        </div>
      )}

      {paymentType && (
        <div className="space-y-4">
          <p className="text-sm font-medium text-foreground">Dados para o pagamento</p>
          {accountEmail && (
            <div className="rounded-lg border border-border bg-muted/20 px-3 py-2">
              <p className="text-xs text-muted-foreground">E-mail da sua conta</p>
              <p className="text-sm text-foreground break-all">{accountEmail}</p>
            </div>
          )}
          <Input
            label="Nome completo"
            value={customerName}
            onChange={e => onCustomerNameChange(maskPersonName(e.target.value))}
            required
          />
          <Input
            label="CPF"
            inputMode="numeric"
            autoComplete="off"
            value={maskCpf(customerCpf)}
            onChange={e => onCustomerCpfChange(cpfDigitsOnly(e.target.value))}
            required
          />
        </div>
      )}

      {sdkLoading && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <div className="h-3 w-3 animate-spin rounded-full border-2 border-muted border-t-primary" />
          Carregando recursos opcionais do Mercado Pago...
        </div>
      )}

      {sdkError && (
        <div className="p-3 text-muted-foreground border border-border rounded bg-muted/20 text-xs">
          O checkout seguirá por redirecionamento. A chave pública do Mercado Pago não
          foi carregada, mas o pagamento continua disponível.
        </div>
      )}

      {errorMessage && (
        <div className="p-3 text-destructive border rounded text-sm">
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export default PaymentMethodFormContent;
